import {
  DeleteOutlined, FormOutlined, RetweetOutlined,
} from '@ant-design/icons';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useParams } from 'react-router';
import { useLocation } from 'react-router-dom';
import _ from 'lodash';
import auth from '../../../utils/auth';
import { useMergeState } from '../../../utils/hooks';
import { toastrSuccess } from '../../../utils/toastNotification';
import NormalButton from '../../components/buttons/normalButton';
import InfoCard from '../../components/cards/infoCard';
import ChangeEmailModal from '../../components/modals/changeEmail';
import ChangePasswordModal from '../../components/modals/changePassword';
import InstructionModal from '../../components/modals/instructionModal';
import {
  deleteUser, getMeInfo, getUserInfo, resetUserPassword,
} from './helper';
import displayLoading from '../../../redux/actions/loading';
import './style.scss';


const UserInfo = (props) => {
  const params = useParams();
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const [state, setState] = useMergeState({
    loading: false,
    deleteVisible: false,
    changePasswordVisible: false,
    editEmailVisible: false,
    data: [],
  });

  const userId = useMemo(() => (location.pathname.includes('/my-profile') ? auth.getLoginData()?.id : params?.id));
  const isMyProfile = useMemo(() => (location.pathname.includes('/my-profile')));

  const toggleDeleteModal = () => {
    setState({ deleteVisible: !state.deleteVisible });
  };

  const onConfirmDelete = async () => {
    const deletedUser = await deleteUser(userId);
    if (deletedUser.isSuccess) {
      toastrSuccess('User deleted successfully');
    }
    history.replace('/user-management');
  };

  const toggleChangePasswordModal = () => {
    setState({ changePasswordVisible: !state.changePasswordVisible });
  };

  const onConfirmResetPassword = async () => {
    const resetPassword = await resetUserPassword(userId);
    if (resetPassword.isSuccess) {
      toastrSuccess('Password reset successfully');
    }
  };

  const toggleEditEmailModal = () => {
    setState({ editEmailVisible: !state.editEmailVisible });
  };

  const onConfirmEditEmail = (newEmail) => {
    const clonedData = _.cloneDeep(state.data);
    clonedData[0].data[1].value = newEmail;
    setState({ data: clonedData });
    auth.setEmail(newEmail);
  };

  useEffect(() => {
    const fetchInitData = async () => {
      if (isMyProfile) {
        const userInfo = await getMeInfo();
        if (userInfo) {
          setState({ data: userInfo });
          return;
        }
      }
      if (userId) {
        const userInfo = await getUserInfo(userId);
        if (!_.isEmpty(userInfo)) {
          setState({ data: userInfo });
        } else {
          history.replace('/user-management');
        }
      }
    };
    dispatch(displayLoading(true));
    fetchInitData();
    dispatch(displayLoading(false));
  }, []);

  return (
    <div
      className={classnames('user-info', props.className)}
    >
      <InfoCard className="w-40" data={state.data} />
      <div className="user-info-header">
        <NormalButton
          text={isMyProfile ? 'Change password' : 'Reset to default password'}
          icon={<RetweetOutlined />}
          onClick={toggleChangePasswordModal}
        />
        {(isMyProfile || auth.isAdmin()) && (
        <NormalButton
          text="Edit email"
          icon={<FormOutlined />}
          onClick={toggleEditEmailModal}
        />
        )}
        {auth.isAdmin() && !isMyProfile && (
        <NormalButton
          text="Delete user"
          type="primary"
          icon={<DeleteOutlined />}
          onClick={toggleDeleteModal}
        />
        )}
      </div>

      <div className="user-info-body mt-32" />
      {isMyProfile ? (
        <ChangePasswordModal
          visible={state.changePasswordVisible}
          onCancel={toggleChangePasswordModal}
        />
      ) : (
        <InstructionModal
          isWarning
          title="Do you want to reset password"
          description="The password of this user's account will be changed into default password and sent to his/her email address."
          visible={state.changePasswordVisible}
          onCancel={toggleChangePasswordModal}
          onConfirm={onConfirmResetPassword}
        />
      )}
      {(isMyProfile || auth.isAdmin()) && (
      <ChangeEmailModal
        visible={state.editEmailVisible}
        onCancel={toggleEditEmailModal}
        data={state?.data[0]?.data}
        onConfirm={onConfirmEditEmail}
      />
      )}
      <InstructionModal
        visible={state.deleteVisible}
        isWarning
        title="Do you want to delete this user"
        description="This user's information will be deleted permanently."
        onConfirm={onConfirmDelete}
        onCancel={toggleDeleteModal}
      />
    </div>

  );
};

UserInfo.defaultProps = {
  className: '',
};
UserInfo.propTypes = {
  className: PropTypes.string,
};

export default UserInfo;
